import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>In everyday situations, it can be interesting to be able to share the same living space, to continue talking and to be able to listen to different audio programs. This is especially desirable in a car context where trips can be long. In such a context, there are different applications that have appealed to car manufacturers:
<ul class=itemize1>
   <li class=itemize><strong><span class=cmbx-10>delivering</span></strong> GPS instructions or car-related information only for the driver,
   <li class=itemize><strong><span class=cmbx-10>directing</span></strong> phone calls only to certain people,
   <li class=itemize><strong><span class=cmbx-10>listening</span></strong> to <strong><span class=cmbx-10>different</span></strong> music programs in different parts of the vehicle, and sometimes not listening to anything when you want to sleep without preventing others from listening to their program.
</ul>
<p>This research and development topic is generally referred to as Personal Sound Zones (PSZ) [1], although there are other names in the scientific literature such as personal sound, personal audio, multizone sound reproduction, individual sound zones, etc. PSZ were first studied by Druyvesteyn and Garas [3] in 1997 and subsequently became a topic of great interest.<br class=newline>Even if the car industry is probably the most studied PSZ application field, other scenarios have also been investigated such as the compensation of hearing loss, avoiding the use of headsets in aircrafts, creating independent zones of music in public places, in a domestic living room, etc.
<h3 class=sectionHead>Problem definition</h3>
<p>
   PSZ consists of reproducing an audio signal with good quality in a given zone of space, generally called the bright zone, while minimizing it in another zone, generally called the dark zone. This can be achieved by using highly directional sound sources or a distributed loudspeaker array (see <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-1.svg data-img-alt="Figure 1. Example of a sound zone set-up.">Figure 1</a>). These two solutions are developed in the following sections.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/29/theory/figure-1.svg alt="Figure 1. Example of a sound zone set-up."><figcaption class=caption>Figure 1. Example of a sound zone set-up.</figcaption></figure>
<h3 class=sectionHead>Beamforming solution</h3>
<p>
   One way to achieve personal sound zones is to use highly directional sound sources, which are oriented towards the targeted listener. In order to improve the rendering, the surfaces facing the main lobe or its reflections should be lined with absorbing materials, see for example <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-2.svg data-img-alt="Figure 2. Example of a directive source installation.">Figure 2</a>, in which the floor and the ceiling offer certain absorbing properties.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/29/theory/figure-2.svg alt="Figure 2. Example of a directive source installation."><figcaption class=caption>Figure 2. Example of a directive source installation.</figcaption></figure>
<p>There are several technical solutions to realize a highly directive source also called sound showers. These are described in the following.
<ul class=itemize1>
   <li class=itemize>the simplest option is to place an omnidirectional loudspeaker at the focal point of a parabola. The sounds are then mainly reflected in the direction of the axis of the parabola,
   <li class=itemize>
      it is also possible to use a parametric array which is made of ultrasonic transducers, generally resonating around 40 kHz. The audio signal is added to a high-level ultrasonic carrier and is demodulated by the non linear propagation which occurs in air at very high level. Such devices allows to obtain narrow, nearly side lobe-free beams of audible sounds.
</ul>
<p>The use of highly directive sources offers some advantages:
<ul class=itemize1>
   <li class=itemize>off-the shelf products: no need to develop a specific signal processing algorithm,
   <li class=itemize>setting up the system is quite simple, you just have to direct the source towards the bright zone, trying to limit the reflections from the main lobe, using for example absorbing materials.
</ul>
<p>However, at low frequencies these sources may lack sound level (parametric antenna) or may not be directive enough (parabolic reflectors). The parametric array also suffers from distortion while the frequency response of the parabolic antenna may not be flat.<br class=newline>
<p>Another possibility is to use a 2D array of loudspeakers and to drive them in such a way as to have a rather narrow main lobe and very attenuated secondary lobes. In this case, dedicated signal processing inspired from the large scientific literature on this topic may be used.
<h3 class=sectionHead>Distributed array solution</h3>
<p>
   The geometry of interest is given in <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-3.svg data-img-alt="Figure 3. Geometry of interest.">Figure 3</a>.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   speakers are used to radiate the sound field while
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
   </math>
   microphones are used to monitor each zone. We will restrict our analysis to harmonic excitation of pulsation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mi>f</mi>
   </math>. The frequency response function of the speaker
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>l</mi>
   </math>
   (with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-rel>≤</mo>
      <mi>l</mi>
      <mo class=MathClass-rel>≤</mo>
      <mi>L</mi>
   </math>) measured by the microphone
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   (with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-rel>≤</mo>
      <mi>m</mi>
      <mo class=MathClass-rel>≤</mo>
      <mi>M</mi>
   </math>) in the bright and dark zones are respectively given by
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>G</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
                  <mi>m</mi>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                        <mi>m</mi>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
            <mspace width="6px"/>
            <mstyle class=mbox>
               <mtext> and </mtext>
            </mstyle>
            <mspace width="6px"/>
            <msub>
               <mrow>
                  <mi>G</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
                  <mi>m</mi>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>D</mi>
                        <mi>m</mi>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>U</mi>
                     </mrow>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
            <mi>m</mi>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   &nbsp;and&nbsp;
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>D</mi>
            <mi>m</mi>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   respectively are the pressure measured by microphone
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   in the bright and in the dark zones when speaker
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>l</mi>
   </math>
   is driven by the voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/29/theory/figure-3.svg alt="Figure 3. Geometry of interest."><figcaption class=caption>Figure 3. Geometry of interest.</figcaption></figure>
<p>The total pressure, <em><span class=cmti-10>i.e.</span></em> when all loudspeakers are on, at a microphone position in the bright and dark zones are respectively given by
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow class=cases>
               <mrow>
                  <mo fence=true form=prefix>{</mo>
                  <mrow>
                     <mtable align=axis style="" equalrows=false columnlines=none equalcolumns=false class=array>
                        <mtr>
                           <mtd class=array columnalign=left>
                              <msub>
                                 <mrow>
                                    <mi>p</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>B</mi>
                                    <mi>m</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-rel>=</mo>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>l</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                              </munderover>
                              <msub>
                                 <mrow>
                                    <mi>p</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>B</mi>
                                    <mi>m</mi>
                                    <mi>l</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-rel>=</mo>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>l</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                              </munderover>
                              <msub>
                                 <mrow>
                                    <mi>U</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>l</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                              <msub>
                                 <mrow>
                                    <mi>G</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>B</mi>
                                    <mi>m</mi>
                                    <mi>l</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mtd>
                        </mtr>
                        <mtr>
                           <mtd class=array columnalign=left>
                              <msub>
                                 <mrow>
                                    <mi>p</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>D</mi>
                                    <mi>m</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-rel>=</mo>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>l</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                              </munderover>
                              <msub>
                                 <mrow>
                                    <mi>p</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>D</mi>
                                    <mi>m</mi>
                                    <mi>l</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                              <mo class=MathClass-rel>=</mo>
                              <munderover accentunder=false accent=false>
                                 <mrow>
                                    <mo>∑</mo>
                                 </mrow>
                                 <mrow>
                                    <mi>l</mi>
                                    <mo class=MathClass-rel>=</mo>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mi>L</mi>
                                 </mrow>
                              </munderover>
                              <msub>
                                 <mrow>
                                    <mi>U</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>l</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                              <msub>
                                 <mrow>
                                    <mi>G</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>D</mi>
                                    <mi>m</mi>
                                    <mi>l</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-open>(</mo>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mtd>
                        </mtr>
                     </mtable>
                  </mrow>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<p>In order to facilitate the calculations, it is possible to use a matrix formalism to express the pressures at all the points of an area using
<table class=equation>
   <tr>
      <td style="font-size: 12px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi mathvariant="bold">G</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant="bold">u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mspace width="6px"/>
            <mstyle class=mbox>
               <mtext> and </mtext>
            </mstyle>
            <mspace width="6px"/>
            <msub>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi mathvariant="bold">G</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant="bold">u</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=italic>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
              <mi>B</mi>
            </mrow>
         </msub>
      </mstyle>

      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mo class=MathClass-open>[</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc>,</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc>,</mo>
            <mi class=MathClass-op>…</mi>
            <mo>⁡</mo>
            <mo class=MathClass-punc>,</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
                  <mi>M</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>]</mo>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=italic>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
              <mi>D</mi>
            </mrow>
         </msub>
      </mstyle>

      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mo class=MathClass-open>[</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc>,</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc>,</mo>
            <mi class=MathClass-op>…</mi>
            <mo>⁡</mo>
            <mo class=MathClass-punc>,</mo>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
                  <mi>M</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>]</mo>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msup>
   </math>
   are complex
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
      <mo class=MathClass-bin>×</mo>
      <mn>1</mn>
   </math>
   vectors giving the pressure at the control points in the bright and dark zones, respectively. The
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
      <mo class=MathClass-bin>×</mo>
      <mn>1</mn>
   </math>
   complex vector
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold-italic>
         <mi mathvariant="bold">u</mi>
      </mstyle>
      <mo class=MathClass-rel>=</mo>
      <msup>
         <mrow>
            <mo class=MathClass-open>[</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc>,</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
            <mo class=MathClass-punc>,</mo>
            <mi class=MathClass-op>…</mi>
            <mo>⁡</mo>
            <mo class=MathClass-punc>,</mo>
            <msub>
               <mrow>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>]</mo>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msup>
   </math>
   represents the speakers’ input voltages. The
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>M</mi>
      <mo class=MathClass-bin>×</mo>
      <mi>L</mi>
   </math>
   matrix entries of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">G</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">G</mi>
         </mrow>
         <mrow>
            <mi>D</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
       <mo>)</mo>
   </math> are respectively given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>G</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
            <mi>m</mi>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>G</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
            <mi>m</mi>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>.<br class=newline>Various algorithms, available in the scientific literature, can be used to calculate the supply voltages of the loudspeakers in order to produce personal sound zones. These algorithms are generally based on constrained optimization methods. In this Lesson, the pressure matching method [2] will be presented. It is based on minimising the following cost function
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=bold-script>ℒ</mi>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant=bold-script>ℒ</mi>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">p</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>−</mo>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">p</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <msub>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>α</mi>
            <msubsup>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>D</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mi>β</mi>
            <msup>
               <mrow>
                  <mi mathvariant="bold">u</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msup>
            <mi mathvariant="bold">u</mi>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
        <mrow>
        </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msup>
   </math>
   is the conjugate transpose. The cost function is composed of three terms:
<ul class=itemize1>
   <li class=itemize>
      the first term
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
               <mo class=MathClass-open>(</mo>
               <msub>
                  <mrow>
                     <mi mathvariant="bold">p</mi>
                  </mrow>
                  <mrow>
                     <mi>B</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>−</mo>
               <msub>
                  <mrow>
                     <mi mathvariant="bold">p</mi>
                  </mrow>
                  <mrow>
                     <mi>t</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mrow>
               <mi>H</mi>
            </mrow>
         </msup>
         <mo class=MathClass-open>(</mo>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
               <mi>B</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>−</mo>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
         <mo class=MathClass-close>)</mo>
      </math>, calculated in the bright zone, is a quadratic error term between a target pressure
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>
      and the pressure field when the personal sound zone system is on. Note that both the amplitude and phase of the reproduced sound field are constrained by this term. The target pressure is chosen by the user and can be a constant pressure within the zone, the pressure radiated by a group of loudspeakers when they are driven in phase, or any other target that meets the constraints of the chosen application,
   <li class=itemize>
      the second term
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msubsup>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>H</mi>
            </mrow>
         </msubsup>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
               <mi>D</mi>
            </mrow>
         </msub>
      </math>
      corresponds to the potential energy in the dark zone. Here, only the amplitude of the pressure is controlled,
   <li class=itemize>
      the last term
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msup>
            <mrow>
               <mi mathvariant="bold">u</mi>
            </mrow>
            <mrow>
               <mi>H</mi>
            </mrow>
         </msup>
         <mi mathvariant="bold">u</mi>
      </math>
      ensures that the speakers are not overpowered by minimizing the energy supplied to them.
</ul>
<p>
   Note that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>β</mi>
   </math>
   coefficients control the relative weight of individual constraints. For example, increasing
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>β</mi>
   </math>
   will force the algorithm to further minimize the energy in the dark zone by relaxing the constraints in the bright zone and on the speaker supply voltages.<br class=newline>
<p>
   By differentiating Eq. (4) with respect to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">u</mi>
   </math>, we can get
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi mathvariant="bold">u</mi>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mo class=MathClass-open>[</mo>
                  <msubsup>
                     <mrow>
                        <mi mathvariant="bold">G</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">G</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
                  <msubsup>
                     <mrow>
                        <mi mathvariant="bold">G</mi>
                     </mrow>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">G</mi>
                     </mrow>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <mi>β</mi>
                  <mi mathvariant="bold">I</mi>
                  <mi mathvariant="bold">d</mi>
                  <mo class=MathClass-close>]</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msup>
            <mo class=MathClass-open>[</mo>
            <msubsup>
               <mrow>
                  <mi mathvariant="bold">G</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msubsup>
            <msub>
               <mrow>
                  <mi mathvariant="bold">p</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>]</mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant="bold">I</mi>
      <mi mathvariant="bold">d</mi>
   </math>
   is a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
      <mo class=MathClass-bin>×</mo>
      <mi>L</mi>
   </math>
   identity matrix.<br class=newline>An example of free field implementation is calculated for an array of 20 equally spaced speakers on a circle of 2 m radius (see <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-4.svg data-img-alt="Figure 4. Implementation geometry.">Figure 4</a>). The bright and dark zones are sampled with a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>4</mn>
      <mo class=MathClass-bin>×</mo>
      <mn>4</mn>
   </math>
   regular grid of microphones with a 8 cm spacing. A pressure map for the frequency of 600 Hz in the speaker plane is shown in <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-5.svg data-img-alt="Figure 5. Sound pressure level mapped in the speaker plane at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>6</mn><mn>0</mn><mn>0</mn></math> Hz.">Figure 5</a>. We notice that, as expected, the sound pressure level in the dark zone is attenuated and the sound pressure level in the bright zone is around 94 dB which corresponds to the target pressure of 1 Pa used here. We also observe that outside the control zones the sound pressure level strongly fluctuates, which is to be expected as the algorithm does not take this area into account. The pressure field outside the control zones is then the result of uncontrolled phase interferences.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/29/theory/figure-4.svg alt="Figure 4. Implementation geometry."><figcaption class=caption>Figure 4. Implementation geometry.</figcaption></figure><figure class=figure><p><img src=${config.ASSETS_URL}/04/29/theory/figure-5.svg alt="Figure 5. Sound pressure level mapped in the speaker plane at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>6</mn><mn>0</mn><mn>0</mn></math> Hz."><figcaption class=caption>Figure 5. Sound pressure level mapped in the speaker plane at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>6</mn><mn>0</mn><mn>0</mn></math> Hz.</figcaption></figure>
<p>
   The speaker input voltage
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   relative to the largest voltage, given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi class=qopname>max</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <msub>
         <mrow>
            <mi>U</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
   </math>, is shown at the top of <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-6.svg data-img-alt="Figure 6. Upper graph: relative input voltage amplitudes (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-bin>∕</mo><mi class=qopname>max</mi><mo>⁡</mo><mo class=MathClass-open>(</mo><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-close>)</mo></math>). Lower graph: <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> phase.">Figure 6</a>. It allows the comparison of the relative contributions of the speakers as well as their relative phases (bottom of <a href="#" data-img-src=${config.ASSETS_URL}/04/29/theory/figure-6.svg data-img-alt="Figure 6. Upper graph: relative input voltage amplitudes (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-bin>∕</mo><mi class=qopname>max</mi><mo>⁡</mo><mo class=MathClass-open>(</mo><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-close>)</mo></math>). Lower graph: <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> phase.">Figure 6</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/29/theory/figure-6.svg alt="Figure 6. Upper graph: relative input voltage amplitudes (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-bin>∕</mo><mi class=qopname>max</mi><mo>⁡</mo><mo class=MathClass-open>(</mo><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-close>)</mo></math>). Lower graph: <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> phase."><figcaption class=caption>Figure 6. Upper graph: relative input voltage amplitudes (<math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-bin>∕</mo><mi class=qopname>max</mi><mo>⁡</mo><mo class=MathClass-open>(</mo><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo><mo class=MathClass-close>)</mo></math>). Lower graph: <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>U</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> phase.</figcaption></figure>
<p>Quality indicators can be calculated to assess the performance of the sound zone algorithms. A classical parameter is the acoustic contrast which corresponds to the ratio of potential energies in the two zones
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>A</mi>
            <mi>C</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mfrac>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi mathvariant="bold">p</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">p</mi>
                     </mrow>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msubsup>
                     <mrow>
                        <mi mathvariant="bold">p</mi>
                     </mrow>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                  </msubsup>
                  <msub>
                     <mrow>
                        <mi mathvariant="bold">p</mi>
                     </mrow>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mstyle class=text>
               <mtext> (dB)</mtext>
            </mstyle>
         </math>
      <td class=eq-no>(6)
</table>
 <p>Perception tests [4] have shown that a contrast of around 30~dB is acceptable to avoid distraction from one audio programme to another. 
<p>Another parameter to quantify the quality of the sound reproduction in the bright zone is the normalized error
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
   </math>
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>E</mi>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <msub>
                                 <mrow>
                                    <mi mathvariant="bold">p</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>B</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-bin>−</mo>
                              <msub>
                                 <mrow>
                                    <mi mathvariant="bold">p</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>t</mi>
                                 </mrow>
                              </msub>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mi>H</mi>
                           </mrow>
                        </msup>
                        <mo class=MathClass-open>(</mo>
                        <msub>
                           <mrow>
                              <mi mathvariant="bold">p</mi>
                           </mrow>
                           <mrow>
                              <mi>B</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-bin>−</mo>
                        <msub>
                           <mrow>
                              <mi mathvariant="bold">p</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi mathvariant="bold">p</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                           <mrow>
                              <mi>H</mi>
                           </mrow>
                        </msubsup>
                        <msub>
                           <mrow>
                              <mi mathvariant="bold">p</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-bin>×</mo>
            <mn>1</mn>
            <mn>0</mn>
            <mn>0</mn>
            <mstyle class=text>
               <mtext> (%)</mtext>
            </mstyle>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   For the tested configuration,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mi>C</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mn>2</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>4</mn>
   </math>
   dB and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>7</mn>
   </math>%. These values can be improved by changing all the parameters of the system:
<ul class=itemize1>
   <li class=itemize>the number of sources and their position,
   <li class=itemize>
      target pressure
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi mathvariant="bold">p</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      coefficients
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>α</mi>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>β</mi>
      </math>.
</ul>
<p>
   The method presented here allows to know the supply voltages of the speakers for a particular frequency. The same method can then be applied for all the frequencies of interest allowing to calculate, by an inverse discrete Fourier transform, the impulse responses (one for each speaker) to be applied to the input audio signal.<br class=newline>The sound zones obtained by this type of method are the results of constructive or destructive interference. When the wavelength becomes small and the propagation medium becomes very complex (with non negligible diffraction and scattering effects), it is then difficult to obtain attractive results (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mi>C</mi>
   </math>
   becomes very weak and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
   </math>
   increases strongly). In practice, sound zone methods by constrained optimization are often limited to the low end of the audible spectrum (up to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
   or
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
   </math>
    kHz). It is then necessary to add another system such as the directive sources described in §3 to obtain a full-band system.<br class=newline>Another constraint is the need to know the functions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">G</mi>
         </mrow>
         <mrow>
            <mi>B</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant="bold">G</mi>
         </mrow>
         <mrow>
            <mi>D</mi>
         </mrow>
      </msub>
   </math>. These must therefore be measured during the calibration phase of the system before calculating the filters.
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, different ways of generating personal sound zones were presented. Regarding the constrained optimization methods, many improvements or variants have been developed or are still under study. Thus, written formulations in the time domain have been proposed [5]. Different cost functions have also been tested allowing to add terms related to the spectral balance, to the causality of the filters obtained, etc. Other works have been focused on the adaptation of the loudspeaker controls when the environment changes [6] (temperature, humidity, listeners’ positions). These methods are still evolving and should lead to different applications in areas such as transportation or consumer audio.
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>2D
  <dd>2-Dimensions
  <dt>GPS
  <dd>Global Positioning System
  <dt>PSZ
  <dd>Personal Sound Zones
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-betlehem2015personal class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Terence Betlehem et al. “Personal sound zones: Delivering interface-free audio to multiple listeners”. In: <span class=cmti-10>IEEE Signal Processing Magazine </span>32.2 (2015), pp. 81–91.
   <dt id=X0-chang2012sound class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Ji-Ho Chang and Finn Jacobsen. “Sound field control with a circular double-layer array of loudspeakers”. In: <span class=cmti-10>The Journal of the Acoustical</span> <span class=cmti-10>Society of America </span>131.6 (2012), pp. 4518–4525.
   <dt id=X0-druyvesteyn1997personal class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Willem F. Druyvesteyn and John Garas. “Personal sound”. In: <span class=cmti-10>Journal of</span> <span class=cmti-10>the Audio Engineering Society </span>45.9 (1997), pp. 685–701.
   <dt id=X0-galvez2015time class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p> Jon Francombe et al. “Elicitation of attributes for the evaluation of audio-on-audio interference”. In: <span class=cmti-10>The Journal of the Acoustical Society of America</span> 136.5 (2014), pp. 2630–2641.
   <dt id=X0-galvez2015time class=thebibliography>[5]
   <dd id=bib-4 class=thebibliography>
      <p>Marcos F Simón Gálvez, Stephen J Elliott, and Jordan Cheer. “Time domain optimization of filters used in a loudspeaker array for personal audio”. In: <span class=cmti-10>IEEE/ACM Transactions on Audio, Speech, and Language</span> <span class=cmti-10>Processing </span>23.11 (2015), pp. 1869–1878.
   <dt id=X0-vindrola2021use class=thebibliography>[6]
   <dd id=bib-5 class=thebibliography>
      <p>Lucas Vindrola et al. “Use of the filtered-x least-mean-squares algorithm to adapt personal sound zones in a car cabin”. In: <span class=cmti-10>The Journal of the</span> <span class=cmti-10>Acoustical Society of America </span>150.3 (2021), pp. 1779–1793.
</dl>`;

export default theory;
